import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { User, Mail, Phone, Shield, Settings, LogOut, ChevronRight, Store, BadgeCheck, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { resolveAvatar } from '../constants/sampleImages';

export const ProfilePage = () => {
  const { userProfile, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isGuest = !userProfile;
  const profile = userProfile ?? {
    displayName: 'Guest User',
    email: 'Sign in to sync your account',
    phone: null,
    photoURL: null,
    role: 'GUEST',
  };

  const handleBecomeVendor = async () => {
    if (!userProfile) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userProfile.uid);
      await updateDoc(userRef, {
        role: 'VENDOR',
        vendorStatus: 'PENDING'
      });
      alert('Application submitted! Our team will review your vendor profile.');
    } catch (error) {
      console.error('Error applying for vendor:', error);
      alert('Failed to submit application.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {isGuest && (
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-700 font-medium">
            You are browsing as a guest. Sign in to save settings and manage your listings.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="shrink-0 bg-primary text-white px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg"
          >
            Sign In
          </button>
        </div>
      )}

      {/* Profile Header */}
      <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm flex flex-col md:flex-row items-center gap-6">
        <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-primary/10 bg-slate-100">
          <img src={resolveAvatar(profile.photoURL)} alt="Avatar" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">{profile.displayName}</h1>
          <p className="text-slate-500 font-medium">{profile.email}</p>
          <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
            <span className="bg-slate-100 text-slate-600 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
              {profile.role}
            </span>
            {profile.role === 'VENDOR' && (
              <span className="bg-primary/5 text-primary text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1">
                <BadgeCheck size={12} /> Verified Seller
              </span>
            )}
          </div>
        </div>
        {!isGuest && (
          <button 
            onClick={() => logout()}
            className="p-3 text-slate-400 hover:text-red-500 transition-colors"
          >
            <LogOut size={24} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Account Details */}
        <div className="space-y-4">
          <h2 className="font-black text-slate-900 uppercase tracking-widest text-sm px-2">Account Settings</h2>
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <SettingsItem icon={User} label="Display Name" value={profile.displayName} />
            <SettingsItem icon={Mail} label="Email Address" value={profile.email} />
            <SettingsItem icon={Phone} label="Phone Number" value={profile.phone || 'Not linked'} />
            <SettingsItem icon={Shield} label="Security" value="Two-factor off" last />
          </div>
        </div>

        {/* Vendor Onboarding */}
        <div className="space-y-4">
          <h2 className="font-black text-slate-900 uppercase tracking-widest text-sm px-2">Business Portal</h2>
          <div className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Store size={120} />
            </div>
            <div className="relative z-10">
              {profile.role === 'VENDOR' ? (
                <>
                  <h3 className="text-xl font-bold mb-2">Vendor Dashboard</h3>
                  <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                    Access your business analytics, manage inventory, and respond to inquiries.
                  </p>
                  <button 
                    onClick={() => navigate('/vendor/dashboard')}
                    className="w-full py-4 bg-primary text-white rounded-2xl font-bold shadow-lg shadow-primary/20 hover:scale-105 transition-all"
                  >
                    Go to Command Center
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-bold mb-2">Grow Your Business</h3>
                  <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                    Join the premier network of Sri Lankan gemstone dealers. Start listing today.
                  </p>
                  <button 
                    onClick={handleBecomeVendor}
                    disabled={loading}
                    className="w-full py-4 bg-white text-slate-900 rounded-2xl font-bold hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Apply'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl flex gap-3">
             <AlertCircle className="text-amber-500 shrink-0" size={20} />
             <p className="text-[10px] text-amber-700 font-medium leading-relaxed uppercase tracking-wider">
                Identity verification is required for all vendors to maintain the highest standards of trust in the marketplace.
             </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsItem = ({ icon: Icon, label, value, last }) => (
  <div className={cn(
    "p-6 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer group",
    !last && "border-b border-slate-100"
  )}>
    <div className="flex items-center gap-4">
      <div className="p-2 bg-slate-100 rounded-xl text-slate-400 group-hover:text-primary group-hover:bg-primary/10 transition-all">
        <Icon size={20} />
      </div>
      <div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="font-bold text-slate-900">{value}</p>
      </div>
    </div>
    <ChevronRight size={18} className="text-slate-300 group-hover:text-primary transition-colors" />
  </div>
);

function cn(...inputs) {
  return inputs.filter(Boolean).join(' ');
}
